import * as React from 'react';
import { default as Draggable, DraggableData } from 'react-draggable';
import Resizable from './Resizable';
import CloseButton from '../common/CloseButton';
import { PopupInfo } from '../../reducers/pages';
import { UIEventListeners } from '../../../../../common/src/ts/types/uievent';
import { Publication } from '../../types/epub';
import { getDefaultRendition } from '../../utils/epub';
import { connect } from 'react-redux';
import { AppStates } from '../../reducers/app';
import SingleItemView, { SingleItemViewOwnProps } from './SingleItemView';
import { UIModeState } from '../../reducers/uimode';
import DrawingCanvasOnTab from './DrawingCanvasOnTab';
import { DrawingToolState, PastingImage } from '../../drawing/reducers/drawingTool';
import { DrawingState, blankDrawingState } from '../../drawing/reducers/drawing';
import { Dispatch, Action } from 'redux';
import { DrawingAction, isSingleAddingAction } from '../../drawing/actions/DrawingAction';
import DrawingToolAction from '../../drawing/actions/DrawingToolAction';
import { DrawingMode } from '../../drawing/types';
import { setPagesIgnorePointerEvents, setUIMode } from '../../actions/UIModeAction';
import { PreferenceState } from '../../reducers/preference';
import { createPageIDForPopup } from '../../helpers/integratedDrawings';
import { MarkerInfo } from '../../marker';
import { PictureStorageState } from '../../drawing/reducers/pictureStorage';
import { UIMode } from '../../../../../common/src/ts/types/UIMode';
import { setFocus, goToReference, setPopupCollapsed } from '../../actions/PageAction';
import {
  MultiroleLinkInfo, MultiroleLinkKind,
} from '../../../../../common/src/ts/types/multiroleLink';
import { Rectangle, SizeConstraint } from 'ark-common';
import { shadowElevationZ24 } from '../../style/effects';
import { getGlobalFrameStore } from '../../services/FrameStore';
import { makeFrameIDForSinglePage } from '../../helpers/pages';
import { zIndex, popupTitlebarHeight } from '../../constants';
import { getMovableBounds, getWindowRect } from '../../utils/view';
import ToolIconButton from '../common/ToolIconButton';
import getStamps from '../../utils/getStamps';

interface PopupOwnProps {
  focus: boolean;
  popupID: string;
  content: PopupInfo;
  onCloseButtonClick: () => void;
  onChangeBounds: (rect: Rectangle) => void;
  onOpenFile: (filePath: string) => void;
  onClose?: (popupInfo: PopupInfo) => void;
  uiEventListeners: UIEventListeners;
  uimode: UIModeState;
}

interface PopupProps extends PopupOwnProps {
  publication: Publication | undefined;
  installedComponents: ReadonlyArray<string>;

  bounds: Rectangle;
  constraint: SizeConstraint;
  collapsed: boolean;
  tabPanelAreaRect: Rectangle;

  preference: PreferenceState;
  dispatch: Dispatch<Action>;
  drawingData: DrawingState;
  pictureStorage: PictureStorageState<HTMLCanvasElement>;
  drawingToolState: DrawingToolState;
  markers: MarkerInfo[];
}

class Popup extends React.Component<PopupProps> {
  public componentDidUpdate() {
    this.notifyPopupRect();
  }

  private notifyPopupRect = () => {
    const frameID = makeFrameIDForSinglePage(this.props.popupID);
    getGlobalFrameStore().notifyPopupRect(frameID, this.props.bounds);
  }

  private handleDragStart = () => {
    setPagesIgnorePointerEvents(true, this.props.dispatch);
  }

  private handleDragEnd = () => {
    setPagesIgnorePointerEvents(false, this.props.dispatch);
  }

  private handleDrag = (_e: MouseEvent | TouchEvent | React.MouseEvent | React.TouchEvent,
                        data: DraggableData) => {
    const { width, height } = this.props.bounds;
    this.props.onChangeBounds({ width, height, x: data.x, y: data.y });
  }

  private renderBody() {
    const { props } = this;
    if (props.publication === undefined) {
      throw new Error('Unexpected state.');
    }

    const rendition = getDefaultRendition(props.publication);
    const pageProps: SingleItemViewOwnProps = {
      item: rendition.itemIndices[props.content.path],
      panelID: props.popupID,
      rootDir: props.publication.resourceDir,
      installedComponents: props.installedComponents,
      uiEventListeners: props.uiEventListeners,
      uimode: props.uimode,
      opf: rendition.opf,
      shouldApplyReflowableContentSettings: false,
      topLevel: props.content.topLevel,
      collapsed: props.collapsed,
      style: this.props.content.frame ? undefined : { backgroundColor: 'transparent' },
    };
    return <SingleItemView {...pageProps} />;
  }

  private handleDrawingAction = (action: DrawingAction) => {
    this.props.dispatch(action);
    if (isSingleAddingAction(action)) {
      setUIMode(UIMode.Touch, this.props.dispatch);
    }
  }

  private handleMultiroleLinkClick = (multiroleLink: MultiroleLinkInfo) => {
    switch (multiroleLink.kind) {
      case MultiroleLinkKind.File:
        this.props.onOpenFile(multiroleLink.file);
        break;
      case MultiroleLinkKind.Web:
        window.open(multiroleLink.url, multiroleLink.title);
        break;
      case MultiroleLinkKind.Page:
        this.props.dispatch(goToReference(multiroleLink.page));
        break;
    }
  }

  private handleSetPastingImagesAction = (pastingImages: ReadonlyArray<PastingImage>) => {
    this.props.dispatch(DrawingToolAction.setPastingImages(pastingImages));
  }

  private handleRequestFocus = () => {
    this.props.dispatch(setFocus(this.props.popupID));
  }

  private renderDrawingCanvas() {
    if (this.props.publication === undefined) {
      throw new Error('Unexpected state.');
    }

    const item = getDefaultRendition(this.props.publication).itemIndices[this.props.content.path];
    if (!item.fxl) {
      return null;
    }

    const drawing = this.props.uimode.mode === UIMode.Drawing;

    return (
      <DrawingCanvasOnTab
        stamps={getStamps(this.props.publication)}
        panelID={this.props.popupID}
        focus={this.props.focus}
        margin={0}
        toolState={{
          ...this.props.drawingToolState,
          mode: drawing ? this.props.drawingToolState.mode : DrawingMode.Disabled,
        }}
        data={this.props.drawingData}
        pictureStorage={this.props.pictureStorage}
        onSetPastingImages={this.handleSetPastingImagesAction}
        onMultiroleLinkClick={this.handleMultiroleLinkClick}
        onRequestFocus={this.handleRequestFocus}
        onDrawingAction={this.handleDrawingAction} />
    );
  }

  private handleCollapseButtonClick = () => {
    const { popupID, collapsed } = this.props;
    this.props.dispatch(setPopupCollapsed(popupID, !collapsed));
  }

  private renderTitlebar(collapsible: boolean) {
    const headerStyle: React.CSSProperties = {
      background: this.props.focus ? '#575757' : '#ababab',
      color: 'white',
      height: popupTitlebarHeight,
      width: this.props.bounds.width,
      flexShrink: 0,
    };
    const h2Style: React.CSSProperties = {
      margin: '10px',
    };
    const collapseButtonStyle: React.CSSProperties = {
      float: 'right',
      minWidth: '24px',
      width: '24px',
      height: '24px',
      marginRight: '8px',
    };

    if (this.props.content.titlebar) {
      return (
        <header style={headerStyle}
          onMouseDown={this.handleRequestFocus} onTouchStart={this.handleRequestFocus}>
          <CloseButton onClick={this.props.onCloseButtonClick} />
          {collapsible
            ? <ToolIconButton
                text={this.props.collapsed ? '□' : '_'}
                style={collapseButtonStyle}
                margin={false}
                onClick={this.handleCollapseButtonClick} />
            : null}
          <h2 style={h2Style}>{this.props.content.title}</h2>
        </header>
      );
    }
    return null;
  }

  private renderWrappedBody() {
    if (this.props.publication === undefined) {
      return;
    }

    const body = <>
      {this.renderBody()}
      {this.renderDrawingCanvas()}
    </>;

    const onResize = (width: number, height: number) => {
      const { x, y } = this.props.bounds;
      this.props.onChangeBounds({ x, y, width, height });
    };

    const { publication, content, bounds: { width, height } } = this.props;

    const item = getDefaultRendition(publication).itemIndices[content.path];
    const { minWidth, minHeight, maxWidth, maxHeight } = this.props.constraint;

    if (content.resizable) {
      return (
        <Resizable width={width} height={height}
          minWidth={minWidth} minHeight={minHeight}
          maxWidth={maxWidth} maxHeight={maxHeight}
          keepAspectRatio={(item.fxl)}
          onResize={onResize}
          onStart={this.handleDragStart} onStop={this.handleDragEnd}>
          {body}
        </Resizable>
      );
    }

    const fixedSize: React.CSSProperties = {
      width: `${this.props.bounds.width}px`,
      height: `${this.props.bounds.height}px`,
      position: 'relative',
      overflow: 'hidden',
    };
    return <div style={fixedSize}>{body}</div>;
  }

  public render() {
    const dialogStyle: React.CSSProperties = {
      position: 'absolute',
      border: this.props.content.frame ? '1px solid gray' : 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    };

    const { bounds, tabPanelAreaRect, content, collapsed } = this.props;
    const elevation = content.modal ? shadowElevationZ24 : null;
    const rootContainerRect = content.topLevel ? getWindowRect() : tabPanelAreaRect;
    const collapsingStyle: React.CSSProperties = {
      ...(collapsed ? { height: `${popupTitlebarHeight}px` } : null),
    };

    const popup = (
      <Draggable
        handle="header"
        onStart={this.handleDragStart}
        onStop={this.handleDragEnd}
        onDrag={this.handleDrag}
        bounds={getMovableBounds(bounds, rootContainerRect)}
        position={{ x: bounds.x, y: bounds.y }}>
        <div style={{ ...dialogStyle, ...elevation, zIndex: content.zIndex, ...collapsingStyle }}>
          {this.renderTitlebar(content.collapsible)}
          {this.renderWrappedBody()}
        </div>
      </Draggable>
    );

    if (content.modal) {
      const backdropStyle: React.CSSProperties = {
        position: 'absolute',
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.3)', // Maybe removed later.
        display: 'flex',
        // zIndex: 5, // mdc-toolbar--fixed has "z-index: 4".

        // Allow header-toolbar and drawing-toolbar operations for development.
        zIndex: zIndex.modalPopup,
      };
      return <aside style={backdropStyle}>{popup}</aside>;
    }
    return popup;
  }

  public componentWillUnmount() {
    const { content, onClose } = this.props;
    if (onClose != null) {
      onClose(content);
    }
  }
}

const mapStateToProps = (state: AppStates, props: PopupOwnProps) => {
  const popupRect = state.view.popupRects[props.popupID];
  if (popupRect === undefined) {
    throw new Error('popup size is not available');
  }

  const popupConstraint = state.pages.popups[props.popupID].constraint;
  if (popupConstraint === undefined) {
    throw new Error('popup constraints is not available');
  }

  const drawingData: DrawingState | undefined =
    state.integratedDrawing.currentData[createPageIDForPopup(props.content)];

  return {
    publication: state.publication,
    installedComponents: state.installedComponents,

    bounds: popupRect,
    constraint: popupConstraint,
    collapsed: state.pages.popups[props.popupID].collapsed,

    preference: state.preference,
    drawingData: drawingData === undefined ? blankDrawingState : drawingData,
    pictureStorage: state.pictureStorage,
    drawingToolState: state.drawingTool,

    tabPanelAreaRect: state.view.tabPanelAreaRect,
  };
};

export default connect(mapStateToProps)(Popup);
